import { getDocumentDisplayLabel, getDocumentProfile } from './documentGroups';

export const VERIFICATION_STATE = {
  /** Документов нет — верификация ещё не начиналась */
  NONE: 'none',
  /** Часть обязательных документов не загружена */
  ACTION: 'action',
  /** Документы отправлены, ждём проверку */
  PENDING: 'pending',
  /** Проверка не пройдена, нужно исправить */
  REJECTED: 'rejected',
  /** Проверка пройдена */
  VERIFIED: 'verified',
} as const;

export type VerificationState = (typeof VERIFICATION_STATE)[keyof typeof VERIFICATION_STATE];

export const VERIFICATION_STATES: string[] = [
  VERIFICATION_STATE.NONE,
  VERIFICATION_STATE.ACTION,
  VERIFICATION_STATE.PENDING,
  VERIFICATION_STATE.REJECTED,
  VERIFICATION_STATE.VERIFIED,
];

/** Из этих состояний владелец может отправить компанию на проверку */
const REQUESTABLE_FROM: string[] = [
  VERIFICATION_STATE.NONE,
  VERIFICATION_STATE.ACTION,
  VERIFICATION_STATE.REJECTED,
];

/**
 * Состояния, о которых знает только бэкенд: заявка отправлена, отклонена или принята.
 * `none` и `action` он тоже отдаёт, но их точнее вычислить по набору документов —
 * иначе владелец не увидит, каких документов не хватает.
 */
const MODERATION_STATES: string[] = [
  VERIFICATION_STATE.PENDING,
  VERIFICATION_STATE.REJECTED,
  VERIFICATION_STATE.VERIFIED,
];

/**
 * Кнопку «Отправить на проверку» показываем владельцу, когда проверка ещё не идёт
 * и все обязательные документы уже загружены — иначе модератору нечего смотреть.
 */
export const canRequestVerification = (state: string, missingCount: number): boolean =>
  REQUESTABLE_FROM.indexOf(state) !== -1 && missingCount === 0;

/**
 * Полный список обязательных документов по всем видимым категориям.
 * Набор зависит от роли компании: грузоотправителю нужно только свидетельство о регистрации.
 */
export const getRequiredDocumentLabels = (companyRole?: string | null): string[] => {
  const { categoryOrder, requiredTypes } = getDocumentProfile(companyRole);

  return categoryOrder.reduce(
    (labels: string[], categoryKey: string) => labels.concat(requiredTypes[categoryKey] || []),
    [],
  );
};

const getUploadedLabels = (documents: any[] = []): Set<string> =>
  new Set(
    documents.map((document) =>
      getDocumentDisplayLabel(document.displayLabel || document.status || document.file_name || ''),
    ),
  );

interface ResolveArgs {
  documents?: any[];
  /**
   * Статус модерации с бэкенда (`companies.verification_status`). `pending`, `rejected`
   * и `verified` знает только он, поэтому они приоритетнее вычисленного состояния.
   */
  apiStatus?: string | null;
  /** Роль компании (`company_roles`) — от неё зависит набор обязательных документов */
  companyRole?: string | null;
}

/**
 * Состояние верификации компании: заявку и решение модератора берём с бэкенда,
 * а «нет документов» / «не хватает документов» выводим из загруженных файлов.
 */
export const resolveVerificationState = ({ documents = [], apiStatus, companyRole }: ResolveArgs) => {
  const uploaded = getUploadedLabels(documents);
  const missing = getRequiredDocumentLabels(companyRole).filter((label) => !uploaded.has(label));

  if (apiStatus && MODERATION_STATES.indexOf(apiStatus) !== -1) {
    return { state: apiStatus as VerificationState, missing };
  }

  if (missing.length > 0) {
    return {
      state: documents.length === 0 ? VERIFICATION_STATE.NONE : VERIFICATION_STATE.ACTION,
      missing,
    };
  }

  // Все обязательные документы на месте, но заявка ещё не отправлена
  return { state: VERIFICATION_STATE.NONE, missing };
};
